/**
 * Figma-style edge re-routing. Instead of anchoring an edge to a fixed handle
 * (which produces awkward opposing-handle connections when the user drags nodes
 * around), we recompute, at render time, which of the four side handles each
 * end should use: the side is chosen by the dominant axis of the center-to-
 * center vector, and the edge anchors at that side's midpoint (where the real
 * `<Handle>` sits). So the connection snaps between the four handles as nodes
 * move, rather than sliding continuously around the shape's perimeter.
 *
 * The dominant-axis choice intentionally mirrors the backend's `bestEdgeHandles`
 * (`shapeHandle` in canvas_edit.go) so agent- and client-routed edges agree.
 *
 * It is purely a render-time computation: no handle/state is written, so
 * dragging updates the connection live with zero persistence churn.
 */

import { type InternalNode, type Node, Position } from "@xyflow/react";

export interface FloatingEdgeParams {
  sx: number;
  sy: number;
  tx: number;
  ty: number;
  sourcePos: Position;
  targetPos: Position;
}

/** Bounding-box geometry pulled from an internal node, or null when unmeasured. */
interface NodeRect {
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
}

function nodeRect(node: InternalNode<Node>): NodeRect | null {
  const w = node.measured?.width ?? node.width ?? undefined;
  const h = node.measured?.height ?? node.height ?? undefined;
  const pos = node.internals.positionAbsolute;
  if (
    w == null ||
    h == null ||
    !Number.isFinite(w) ||
    !Number.isFinite(h) ||
    w <= 0 ||
    h <= 0 ||
    !pos ||
    !Number.isFinite(pos.x) ||
    !Number.isFinite(pos.y)
  ) {
    return null;
  }
  return { x: pos.x, y: pos.y, w, h, cx: pos.x + w / 2, cy: pos.y + h / 2 };
}

/**
 * The side of `from` facing `to`, chosen by the dominant axis of the
 * center-to-center vector. Mirrors `shapeHandle` in canvas_edit.go: horizontal
 * dominance → right/left, vertical dominance → bottom/top (ties favor
 * horizontal, matching `>=`).
 */
function bestSide(from: NodeRect, to: NodeRect): Position {
  const dx = to.cx - from.cx;
  const dy = to.cy - from.cy;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0 ? Position.Right : Position.Left;
  }
  return dy >= 0 ? Position.Bottom : Position.Top;
}

/** Midpoint of `rect`'s `side` — where that side's `<Handle>` is anchored. */
function sideMidpoint(
  rect: NodeRect,
  side: Position,
): { x: number; y: number } {
  switch (side) {
    case Position.Left:
      return { x: rect.x, y: rect.cy };
    case Position.Right:
      return { x: rect.x + rect.w, y: rect.cy };
    case Position.Top:
      return { x: rect.cx, y: rect.y };
    case Position.Bottom:
      return { x: rect.cx, y: rect.y + rect.h };
  }
}

/**
 * Compute the best handle/side pair + their midpoint anchors for an edge
 * between two nodes. Returns null when either node isn't measured yet (caller
 * should fall back to React Flow's handle-derived coordinates).
 */
export function getFloatingEdgeParams(
  source: InternalNode<Node> | undefined,
  target: InternalNode<Node> | undefined,
): FloatingEdgeParams | null {
  if (!source || !target) return null;
  const s = nodeRect(source);
  const t = nodeRect(target);
  if (!s || !t) return null;
  // Fully overlapping centers have no meaningful direction; bail to the
  // handle-based fallback rather than picking an arbitrary side.
  if (s.cx === t.cx && s.cy === t.cy) return null;

  const sourcePos = bestSide(s, t);
  const targetPos = bestSide(t, s);
  const sp = sideMidpoint(s, sourcePos);
  const tp = sideMidpoint(t, targetPos);
  return {
    sx: sp.x,
    sy: sp.y,
    tx: tp.x,
    ty: tp.y,
    sourcePos,
    targetPos,
  };
}
